<script lang="ts">
export interface IconPickerTabIconProps {
	readonly?: boolean;
	disabled?: boolean;
	effectiveColor: string;
}

const iconDataCache = new Map<string, { props: Record<string, string | number> }>();
</script>

<script lang="ts" setup>
import { buildIcon, getIcon } from '@iconify/vue';
import { chunk } from 'lodash-es';
import { computed, h, nextTick, unref, useId, useTemplateRef, watch } from 'vue';
import { RecycleScroller } from 'vue-virtual-scroller';
import { useGridNavigation } from '#layers/ui/composables/use-grid-navigation';
import { useIconSearch } from '#layers/ui/composables/use-icon-search';
import IconPickerEmptyState from './IconPickerEmptyState.vue';

const props = defineProps<IconPickerTabIconProps>();

const emit = defineEmits<{
	select: [iconName: string];
}>();

const icon = defineModel<string | null>();

function getCachedIconData(name: string) {
	const cached = iconDataCache.get(name);
	if (cached) return cached;

	const raw = getIcon(name);
	if (!raw) return null;

	const { body, attributes } = buildIcon(raw);
	const data = {
		props: { ...attributes, 'class': 'size-full', 'aria-hidden': 'true', 'focusable': 'false', 'innerHTML': body },
	};

	iconDataCache.set(name, data);
	return data;
}

function RawIcon(iconProps: { name: string }) {
	const data = getCachedIconData(iconProps.name);
	if (!data) return null;
	return h('svg', data.props);
}

const { search, displayedIcons } = useIconSearch();

const gridRef = useTemplateRef<HTMLElement>('gridRef');
const scrollerRef = useTemplateRef<{ scrollToItem: (index: number, options?: { align?: string }) => void }>('scroller');
const searchInputRef = useTemplateRef('searchInput');

const ICON_SIZE = 28;
const COLUMNS = 14;

const rows = computed(() =>
	chunk(displayedIcons.value, COLUMNS).map((icons, id) => ({ id, icons })),
);

function getGlobalIndex(rowIndex: number, colIndex: number) {
	return rowIndex * COLUMNS + colIndex;
}

const isDisabled = computed(() => Boolean(props.readonly || props.disabled));

const {
	focusedIndex,
	setFocused,
	handleArrowDown,
	handleArrowLeft,
	handleArrowRight,
	handleArrowUp,
	handleActivate,
} = useGridNavigation({
	columns: COLUMNS,
	total: () => displayedIcons.value.length,
	disabled: isDisabled,
	wrapHorizontal: true,
	onActivate: () => {
		const entry = displayedIcons.value[focusedIndex.value];
		if (entry) selectIcon(entry.name);
	},
});

watch(search, () => {
	setFocused(-1);
});

function scrollToRow(rowIndex: number) {
	const scroller = scrollerRef.value;

	if (scroller && typeof scroller.scrollToItem === 'function') {
		scroller.scrollToItem(rowIndex);
	}
}

watch(focusedIndex, (index) => {
	if (index < 0) return;
	scrollToRow(Math.floor(index / COLUMNS));
});

async function focusSearchInput() {
	setFocused(-1);
	await nextTick();
	unref(searchInputRef.value?.inputRef)?.focus();
}

defineExpose({
	focusSearchInput,
});

function handleSearchKeydown(event: KeyboardEvent) {
	if (event.key === 'ArrowDown' && displayedIcons.value.length > 0) {
		event.preventDefault();
		setFocused(0);
		gridRef.value?.focus();
	}
}

function handleGridKeydown(event: KeyboardEvent) {
	if (displayedIcons.value.length === 0) return;

	switch (event.key) {
		case 'ArrowRight':
			event.preventDefault();
			handleArrowRight(event);
			return;
		case 'ArrowLeft':
			event.preventDefault();
			handleArrowLeft(event);
			return;
		case 'ArrowDown':
			event.preventDefault();
			handleArrowDown(event);
			return;
		case 'ArrowUp':
			event.preventDefault();

			if (focusedIndex.value < COLUMNS) {
				focusSearchInput();
				return;
			}

			handleArrowUp(event);
			return;
		case 'Enter':
			event.preventDefault();
			handleActivate(event);
			return;
		case 'Escape':
			event.preventDefault();
			focusSearchInput();
			return;
		default:
			if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
				focusSearchInput();
			}
	}
}

function selectIcon(iconName: string) {
	if (props.readonly || props.disabled) return;
	emit('select', iconName);
}

const listboxId = useId();

const focusedIconId = computed(() => {
	const entry = displayedIcons.value[focusedIndex.value];
	return entry ? `${listboxId}-${entry.name}` : undefined;
});
</script>

<template>
	<DInput
		ref="searchInput"
		v-model="search"
		:placeholder="$t('icon_picker.search_placeholder')"
		variant="none"
		class="w-full border-b border-default"
		size="md"
		autofocus
		:ui="{ base: 'h-11 px-4' }"
		:readonly
		:disabled
		@keydown="handleSearchKeydown"
	/>

	<div
		ref="gridRef"
		role="listbox"
		:aria-activedescendant="focusedIconId"
		:aria-label="$t('icon_picker.select_icon')"
		tabindex="-1"
		class="flex min-h-0 flex-1 flex-col overflow-hidden outline-none"
		:style="{ '--icon-color': props.effectiveColor }"
		@keydown="handleGridKeydown"
	>
		<IconPickerEmptyState v-if="rows.length === 0">
			{{ $t('icon_picker.no_results') }}
		</IconPickerEmptyState>

		<RecycleScroller
			v-else
			ref="scroller"
			:items="rows"
			:item-size="ICON_SIZE"
			key-field="id"
			direction="vertical"
			class="
				flex-1 scrollbar-thin [scrollbar-color:var(--ui-border-highlighted)_transparent] overflow-x-hidden overflow-y-auto
				overscroll-none p-1 px-1
			"
		>
			<template #default="{ item: row, index: rowIndex }">
				<div class="grid grid-cols-14">
					<div
						v-for="(entry, colIndex) in row.icons"
						:id="`${listboxId}-${entry.name}`"
						:key="entry.name"
						role="option"
						class="
							inline-flex h-7 w-full cursor-pointer items-center justify-center rounded-sm p-1.5 text-(--icon-color)
							hover:bg-(--icon-color)/10
							aria-disabled:pointer-events-none aria-disabled:opacity-50
							aria-selected:bg-(--icon-color)/10
						"
						:class="{ 'bg-(--icon-color)/10': getGlobalIndex(rowIndex, colIndex) === focusedIndex }"
						:aria-label="entry.label"
						:aria-selected="entry.name === icon || undefined"
						:aria-disabled="readonly || disabled || undefined"
						@click="selectIcon(entry.name)"
					>
						<RawIcon :name="entry.name" />
					</div>
				</div>
			</template>
		</RecycleScroller>
	</div>
</template>
